/**
 * Client-side mirror of server/utils/roles.js. The server is the enforcement
 * point — restricted roles get 403s and redacted payloads from the API
 * regardless of what this file says — so this only keeps the UI from offering
 * what the API will refuse.
 *
 * Restricted roles see no revenue figures and take no billing actions:
 *   manager           Subscriptions, Customers and Customer Health (including
 *                     the detail pages under them).
 *   customer_success  The manager pages plus Trials and Deactivations (the
 *                     History and Billing health tabs), and per-customer
 *                     billing records (billing history, payment totals).
 */

export const ROLE_MANAGER = "manager";
export const ROLE_CUSTOMER_SUCCESS = "customer_success";

// Path prefixes each restricted role may open. Anything else redirects to
// their home page.
const MANAGER_PATH_PREFIXES = ["/subscriptions", "/customers", "/customer-health"];
const RESTRICTED_PATH_PREFIXES = {
  [ROLE_MANAGER]: MANAGER_PATH_PREFIXES,
  [ROLE_CUSTOMER_SUCCESS]: [...MANAGER_PATH_PREFIXES, "/trials", "/deactivations"],
};

export function isManager(user) {
  return user?.role === ROLE_MANAGER;
}

export function isRestrictedRole(user) {
  return Object.hasOwn(RESTRICTED_PATH_PREFIXES, user?.role ?? "");
}

export function canAccessPath(user, pathname) {
  if (!isRestrictedRole(user)) return true;
  return RESTRICTED_PATH_PREFIXES[user.role].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function homePathFor(user) {
  return isRestrictedRole(user) ? "/customers" : "/dashboard";
}

/** Whether this user may see revenue figures (MRR, revenue summaries, reports). */
export function canViewFinancials(user) {
  return !isRestrictedRole(user);
}

/**
 * Whether this user may see per-customer billing records: invoices, past-due
 * accounts (Billing health) and deactivation payment totals. Wider than
 * canViewFinancials — customer_success gets these but no revenue figures.
 */
export function canViewBilling(user) {
  return !isManager(user);
}
